import { getLastKnownAuthUser } from "./authService";
import {
  deleteLocalWithdrawal,
  deleteRecoverySnapshot,
  getAllLocalWithdrawals,
  getLocalWithdrawal,
  saveLocalWithdrawal,
  saveRecoverySnapshot,
} from "../storage/offlineDatabase";
import type { LocalWithdrawalData, LocalWithdrawalRecord } from "../types/offlineWithdrawal";
import { createWithdrawalDocumentArtifacts } from "./withdrawalDocumentService";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { nextLocalWithdrawalNumber } from "./withdrawalIdentityService";

export type SyncSimulationPhase =
  | "backend_check"
  | "recovery_write"
  | "metadata_create_or_resume"
  | "server_files"
  | "pdf_upload"
  | "preview_upload"
  | "label_upload"
  | "complete"
  | "email";

export type SyncSimulationErrorType =
  | "connectivity"
  | "timeout"
  | "http500"
  | "http503"
  | "http401"
  | "http403"
  | "http409"
  | "blob"
  | "recovery"
  | "email";

export type SyncSimulationMode = "once" | "until9" | "direct9";

export interface SyncFailureSimulationConfig {
  enabled: boolean;
  testClientUuid: string | null;
  phase: SyncSimulationPhase;
  errorType: SyncSimulationErrorType;
  mode: SyncSimulationMode;
  triggerCount: number;
  createdAt: string;
}

export interface DiagnosticTestCandidate {
  clientUuid: string;
  withdrawalNumber: number | null;
  serverId: number | null;
  local: boolean;
  server: boolean;
}

export const DIAGNOSTIC_MATERIAL_NUMBER = "9999999999";
export const DIAGNOSTIC_MATERIAL_TEXT_PREFIX = "DIAGNOSE-SYNC-TEST";

const STORAGE_KEY = "materialentnahme:admin-sync-failure-simulation:v1";
const HIDDEN_HISTORY_KEY = "materialentnahme:hidden-diagnostic-history:v1";

const DEFAULT_CONFIG: SyncFailureSimulationConfig = {
  enabled: false,
  testClientUuid: null,
  phase: "pdf_upload",
  errorType: "http500",
  mode: "until9",
  triggerCount: 0,
  createdAt: new Date(0).toISOString(),
};

export function getSyncFailureSimulation(): SyncFailureSimulationConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_CONFIG, ...(JSON.parse(raw) as Partial<SyncFailureSimulationConfig>) } : { ...DEFAULT_CONFIG };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

export function saveSyncFailureSimulation(config: SyncFailureSimulationConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  window.dispatchEvent(new Event("materialentnahme:sync-simulation-updated"));
}

export function disableSyncFailureSimulation(): void {
  const current = getSyncFailureSimulation();
  saveSyncFailureSimulation({ ...current, enabled: false });
}

function hiddenHistoryUuids(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_HISTORY_KEY);
    const values = raw ? JSON.parse(raw) as string[] : [];
    return new Set(values.filter(Boolean));
  } catch {
    return new Set();
  }
}

export function isDiagnosticHistoryHidden(clientUuid: string): boolean {
  return hiddenHistoryUuids().has(clientUuid);
}

export function hideDiagnosticHistory(clientUuid: string): void {
  const next = hiddenHistoryUuids();
  next.add(clientUuid);
  localStorage.setItem(HIDDEN_HISTORY_KEY, JSON.stringify([...next]));
}

export async function syncHiddenDiagnosticHistoryToServer(): Promise<void> {
  const pending = [...hiddenHistoryUuids()];
  if (pending.length === 0) return;
  await Promise.allSettled(pending.map(async (clientUuid) => {
    const response = await fetch(`/api/withdrawals/admin/diagnostic-tests/${encodeURIComponent(clientUuid)}/hide`, {
      method: "POST", credentials: "include", cache: "no-store",
    });
    if (!response.ok && response.status !== 404) throw new Error(`Diagnose-Ausblendung fehlgeschlagen (${response.status}).`);
  }));
}

export function isActiveDiagnosticSimulationForUuid(clientUuid: string): boolean {
  const config = getSyncFailureSimulation();
  return Boolean(config.enabled && config.testClientUuid === clientUuid);
}

export function isDiagnosticTestData(data: Pick<LocalWithdrawalData, "material_number" | "material_text">): boolean {
  return data.material_number === DIAGNOSTIC_MATERIAL_NUMBER
    && data.material_text.startsWith(DIAGNOSTIC_MATERIAL_TEXT_PREFIX);
}

export function isDiagnosticTestRecord(record: LocalWithdrawalRecord): boolean {
  return isDiagnosticTestData(record.data);
}

export function isDiagnosticTestHistoryItem(item: { material_number: string; material_text: string }): boolean {
  return item.material_number === DIAGNOSTIC_MATERIAL_NUMBER
    && item.material_text.startsWith(DIAGNOSTIC_MATERIAL_TEXT_PREFIX);
}

function simulatedError(errorType: SyncSimulationErrorType): Error {
  switch (errorType) {
    case "connectivity": return new Error("SIMULATION · Server nicht erreichbar.");
    case "timeout": return new Error("SIMULATION · Zeitüberschreitung beim Serveraufruf.");
    case "http500": return new Error("SIMULATION · HTTP 500 · Interner Serverfehler.");
    case "http503": return new Error("SIMULATION · HTTP 503 · Dienst vorübergehend nicht verfügbar.");
    case "http401": return new Error("SIMULATION · HTTP 401 · Sitzung ungültig oder abgelaufen.");
    case "http403": return new Error("SIMULATION · HTTP 403 · Keine Berechtigung.");
    case "http409": return new Error("SIMULATION · HTTP 409 · Konflikt beim Serverzustand.");
    case "blob": return new Error("The object can not be found here.");
    case "recovery": return new Error("SIMULATION · Recovery-Schutzkopie konnte nicht gespeichert werden.");
    case "email": return new Error("DIAGNOSE-TEST · echter E-Mail-Versand wird verwendet.");
  }
}

export function shouldSimulateFor(record: LocalWithdrawalRecord, phase: SyncSimulationPhase): boolean {
  const config = getSyncFailureSimulation();
  if (phase === "email") return false;
  if (!config.enabled || !config.testClientUuid || config.testClientUuid !== record.client_uuid || config.phase !== phase) return false;
  if (config.mode === "direct9") return false;
  if (config.mode === "once" && config.triggerCount >= 1) return false;
  if (config.mode === "until9" && config.triggerCount >= 9) return false;
  return true;
}

export function maybeThrowSimulatedSyncFailure(record: LocalWithdrawalRecord, phase: SyncSimulationPhase): void {
  if (!shouldSimulateFor(record, phase)) return;
  const config = getSyncFailureSimulation();
  saveSyncFailureSimulation({ ...config, triggerCount: config.triggerCount + 1 });
  throw simulatedError(config.errorType);
}

function diagnosticLocalPhase(phase: SyncSimulationPhase): NonNullable<LocalWithdrawalRecord["sync_phase"]> {
  return phase === "email" ? "complete" : phase;
}

export async function forceDiagnosticSimulationAttempt(record: LocalWithdrawalRecord, config: SyncFailureSimulationConfig): Promise<LocalWithdrawalRecord> {
  if (!isDiagnosticTestRecord(record)) throw new Error("Nur Diagnose-Testdatensätze dürfen künstlich hochgezählt werden.");
  const nextAttempt = Math.min(9, (record.sync_attempt_count ?? 0) + 1);
  const error = simulatedError(config.errorType);
  const now = new Date().toISOString();
  const next: LocalWithdrawalRecord = {
    ...record,
    sync_status: "error",
    sync_attempt_count: nextAttempt,
    next_sync_attempt_at: null,
    last_sync_attempt_at: now,
    last_error: error.message,
    sync_failure_phase: diagnosticLocalPhase(config.phase),
    sync_phase: diagnosticLocalPhase(config.phase),
    updated_at: now,
  };
  await saveLocalWithdrawal(next);
  const current = getSyncFailureSimulation();
  saveSyncFailureSimulation({ ...current, triggerCount: Math.max(current.triggerCount + 1, nextAttempt) });
  return next;
}

export async function forceDiagnosticSimulationToNine(record: LocalWithdrawalRecord, config: SyncFailureSimulationConfig): Promise<LocalWithdrawalRecord> {
  if (!isDiagnosticTestRecord(record)) throw new Error("Nur Diagnose-Testdatensätze dürfen direkt auf 9/9 gesetzt werden.");
  const error = simulatedError(config.errorType);
  const now = new Date().toISOString();
  const next: LocalWithdrawalRecord = {
    ...record,
    sync_status: "error",
    sync_attempt_count: 9,
    next_sync_attempt_at: null,
    last_sync_attempt_at: now,
    last_error: error.message,
    sync_failure_phase: diagnosticLocalPhase(config.phase),
    sync_phase: diagnosticLocalPhase(config.phase),
    updated_at: now,
  };
  await saveLocalWithdrawal(next);
  const current = getSyncFailureSimulation();
  saveSyncFailureSimulation({ ...current, triggerCount: 9 });
  return next;
}

// Rückwärtskompatibel für den Sync-Service. Diagnose-E-Mails werden nicht mehr
// künstlich als Fehler markiert, sondern nur beim expliziten E-Mail-Test real versendet.
export function simulatedEmailError(_record: LocalWithdrawalRecord): string | null {
  void _record;
  return null;
}

export function isDiagnosticEmailTest(record: LocalWithdrawalRecord): boolean {
  const config = getSyncFailureSimulation();
  return Boolean(
    config.enabled
    && config.testClientUuid === record.client_uuid
    && config.phase === "email"
    && isDiagnosticTestRecord(record)
  );
}

function uuid(): string {
  return crypto.randomUUID?.() ?? `diag-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function createDiagnosticSyncTestRecord(): Promise<LocalWithdrawalRecord> {
  const owner = getLastKnownAuthUser();
  if (!owner || owner.role !== "admin") throw new Error("Der Diagnose-Test ist ausschließlich für Administratoren verfügbar.");
  const clientUuid = uuid();
  const now = new Date().toISOString();
  const today = now.slice(0, 10);
  const localNumber = await nextLocalWithdrawalNumber(owner.id);
  const data: LocalWithdrawalData = {
    client_uuid: clientUuid,
    withdrawal_date: today,
    material_number: DIAGNOSTIC_MATERIAL_NUMBER,
    material_text: `${DIAGNOSTIC_MATERIAL_TEXT_PREFIX} – NICHT FACHLICH VERWENDEN`,
    material_type: "DIAG-TEST",
    building: "62",
    floor: "O",
    cabinet: "999",
    compartment: "99",
    sub_compartment: "99",
    quantity_removed: "1",
    current_stock: "1",
    accounting_system: "Diagnose",
    accounting_subarea: "Sync-Test",
    accounting_number: "999999999",
    accounting_manufacturer: "Diagnose",
    order_info: "Automatisch erzeugter Diagnose-Testdatensatz",
    remark: "DIAGNOSETEST · darf ausschließlich für Fehlersimulation verwendet werden.",
    persons: [{ name: owner.username }],
  };
  const artifacts = await createWithdrawalDocumentArtifacts({
    withdrawalDate: data.withdrawal_date,
    materialNumber: data.material_number,
    materialText: data.material_text,
    materialType: data.material_type ?? "",
    storageAddress: `${data.building}${data.floor} ${data.cabinet} ${data.compartment} ${data.sub_compartment}`,
    quantityRemoved: data.quantity_removed,
    currentStock: data.current_stock,
    persons: data.persons.map((person) => person.name),
    accountingNumber: data.accounting_number ?? "",
    accountingDescription: `${data.accounting_system ?? ""} - ${data.accounting_subarea ?? ""}`,
    orderInfo: data.order_info ?? "",
  });
  const record: LocalWithdrawalRecord = {
    client_uuid: clientUuid,
    record_type: "completed",
    owner_user_id: owner.id,
    owner_username: owner.username,
    local_withdrawal_number: localNumber,
    data,
    pdf_blob: artifacts.pdf,
    pdf_filename: `DIAG_SYNC_${Date.now()}.pdf`,
    pdf_preview_blob: artifacts.preview,
    pdf_preview_filename: `DIAG_SYNC_${Date.now()}_Vorschau.webp`,
    label_blob: null,
    label_filename: null,
    debug_image_blob: null,
    debug_image_filename: null,
    debug_data_blob: null,
    debug_data_filename: null,
    sync_status: "pending",
    server_withdrawal_id: null,
    server_withdrawal_number: null,
    base_version: null,
    created_at: now,
    updated_at: now,
    last_sync_attempt_at: null,
    sync_attempt_count: 0,
    next_sync_attempt_at: null,
    synced_at: null,
    last_error: null,
    sync_phase: "idle",
    sync_trace: [],
  };
  await saveLocalWithdrawal(record);
  await saveRecoverySnapshot(record);
  return record;
}

export async function listDiagnosticTestCandidates(): Promise<DiagnosticTestCandidate[]> {
  const byUuid = new Map<string, DiagnosticTestCandidate>();
  const localRecords = await getAllLocalWithdrawals().catch(() => []);
  for (const record of localRecords) {
    if (!isDiagnosticTestRecord(record) || isDiagnosticHistoryHidden(record.client_uuid)) continue;
    byUuid.set(record.client_uuid, {
      clientUuid: record.client_uuid,
      withdrawalNumber: record.server_withdrawal_number ?? record.local_withdrawal_number,
      serverId: record.server_withdrawal_id,
      local: true,
      server: Boolean(record.server_withdrawal_id),
    });
  }

  try {
    const response = await fetchWithTimeout(
      `/api/withdrawals/history?q=${encodeURIComponent(DIAGNOSTIC_MATERIAL_NUMBER)}&filter=all&offset=0&limit=100`,
      { credentials: "include", cache: "no-store" },
      1200,
    );
    if (response.ok) {
      const payload = await response.json() as { items?: Array<{ id:number; uuid:string; withdrawal_number:number|null; material_number:string; material_text:string }> };
      for (const item of payload.items ?? []) {
        if (!isDiagnosticTestHistoryItem(item) || isDiagnosticHistoryHidden(item.uuid)) continue;
        const previous = byUuid.get(item.uuid);
        byUuid.set(item.uuid, {
          clientUuid: item.uuid,
          withdrawalNumber: item.withdrawal_number ?? previous?.withdrawalNumber ?? null,
          serverId: item.id,
          local: previous?.local ?? false,
          server: true,
        });
      }
    }
  } catch { /* Offline: lokale Kandidaten reichen für die Bereinigung. */ }

  return [...byUuid.values()].sort((a, b) => (b.withdrawalNumber ?? -1) - (a.withdrawalNumber ?? -1));
}

export async function cleanupDiagnosticTestData(clientUuid: string): Promise<void> {
  const record = await getLocalWithdrawal(clientUuid).catch(() => null);
  if (record && !isDiagnosticTestRecord(record)) throw new Error("Der ausgewählte Vorgang ist kein Diagnose-Testdatensatz.");

  // Tombstone zuerst setzen: selbst wenn das Backend offline ist, darf der
  // verbliebene Serverdatensatz anschließend auf diesem Gerät nicht wieder
  // in den Verlauf eingemischt werden. Der Serverdatensatz selbst bleibt bestehen.
  hideDiagnosticHistory(clientUuid);
  await Promise.allSettled([deleteRecoverySnapshot(clientUuid), deleteLocalWithdrawal(clientUuid)]);

  // Fehlerreport nur best effort quittieren. Die lokale Bereinigung wartet
  // ausdrücklich nicht auf ein langsames oder offline Backend.
  void fetch(`/api/notices/sync-failure/${clientUuid}/resolve`, { method: "POST", credentials: "include" }).catch(() => undefined);
  void fetch(`/api/withdrawals/admin/diagnostic-tests/${encodeURIComponent(clientUuid)}/hide`, { method: "POST", credentials: "include" }).catch(() => undefined);

  const current = getSyncFailureSimulation();
  if (current.testClientUuid === clientUuid) {
    saveSyncFailureSimulation({ ...DEFAULT_CONFIG, createdAt: new Date().toISOString() });
  }
  window.dispatchEvent(new Event("materialentnahme:status-refresh"));
}

export async function getDiagnosticSyncTestRecord(clientUuid: string | null): Promise<LocalWithdrawalRecord | null> {
  return clientUuid ? getLocalWithdrawal(clientUuid) : null;
}

export function phaseLabel(phase: SyncSimulationPhase): string {
  const labels: Record<SyncSimulationPhase, string> = {
    backend_check: "Serverprüfung",
    recovery_write: "Recovery schreiben",
    metadata_create_or_resume: "Serverdatensatz anlegen/fortsetzen",
    server_files: "Serverdateien prüfen",
    pdf_upload: "PDF-Upload",
    preview_upload: "Vorschau-Upload",
    label_upload: "Kontrollbild-Upload",
    complete: "Sync abschließen",
    email: "E-Mail-Test – echter Versand",
  };
  return labels[phase];
}

export function errorTypeLabel(type: SyncSimulationErrorType): string {
  const labels: Record<SyncSimulationErrorType, string> = {
    connectivity: "Backend nicht erreichbar",
    timeout: "Timeout",
    http500: "HTTP 500 – Serverfehler",
    http503: "HTTP 503 – Dienst nicht verfügbar",
    http401: "HTTP 401 – Sitzung ungültig",
    http403: "HTTP 403 – Keine Berechtigung",
    http409: "HTTP 409 – Konflikt",
    blob: "Defekter PDF-Blob / Safari-Fehler",
    recovery: "Recovery-Schreibfehler",
    email: "E-Mail-Test – echter SMTP-Versand",
  };
  return labels[type];
}

export function simulationExpectation(config: SyncFailureSimulationConfig): string {
  if (config.phase === "email") {
    return "Der Diagnose-Vorgang wird vollständig synchronisiert. Nur bei diesem Test wird anschließend eine echte, deutlich als DIAGNOSE-TEST gekennzeichnete E-Mail über Worker und SMTP versendet. Es wird kein künstlicher E-Mail-Fehler erzeugt.";
  }
  const countText = config.mode === "direct9" ? "direkt als 9/9-Endzustand gesetzt" : config.mode === "until9" ? "automatisch von 0/9 bis 9/9 wiederholt" : "einmal ausgelöst";
  const retryText = config.mode === "until9" || config.mode === "direct9"
    ? "Im Diagnosemodus wird die 9/9-Grenze bewusst unabhängig von der realen Backend-Erreichbarkeit simuliert; die produktive Retry-Logik bleibt unverändert."
    : (config.errorType === "connectivity" || config.errorType === "timeout"
      ? "Im normalen Einmal-Test bleibt die produktive Connectivity-Regel erhalten."
      : "Retryfähige Fehler dürfen entsprechend der Retry-Regeln weiterlaufen.");
  return `${phaseLabel(config.phase)} · ${errorTypeLabel(config.errorType)} wird ${countText}. ${retryText} Danach müssen Verlauf, lokale Diagnose und Sync-Fehlerreport denselben Zustand zeigen.`;
}

export const SYNC_SIMULATION_PHASES: SyncSimulationPhase[] = ["backend_check", "recovery_write", "metadata_create_or_resume", "server_files", "pdf_upload", "preview_upload", "label_upload", "complete", "email"];
export const SYNC_SIMULATION_ERROR_TYPES: SyncSimulationErrorType[] = ["connectivity", "timeout", "http500", "http503", "http401", "http403", "http409", "blob", "recovery", "email"];
